import React from 'react';
import PropTypes from 'prop-types';
import styles from './Skeleton.module.scss';

/* ────────────────────────────────────────────────────────────
 * Skeleton — shimmering placeholder block.
 *
 * The contract (MOTION.md §4.7):
 *   - First-paint loading state uses Skeleton primitives shaped
 *     like the eventual content. Never a centered <Loader/>
 *     spinner for first paint.
 *   - The spinner is reserved for in-flight actions inside an
 *     already-rendered surface (button submitting, refresh icon
 *     spinning).
 *
 * Default usage:
 *   <Skeleton width="60%" height="1rem" radius="sm" />
 *
 * For tables, use <SkeletonRow columns={n} /> below.
 * For tile grids, use <SkeletonCard />.
 * ──────────────────────────────────────────────────────────── */

const RADIUS_MAP = {
  sm: styles.radiusSm,
  md: styles.radiusMd,
  lg: styles.radiusLg,
  pill: styles.radiusPill,
  circle: styles.radiusCircle,
};

const Skeleton = ({ width, height, radius, className, style, ariaLabel }) => {
  const classNames = [styles.skeleton, RADIUS_MAP[radius], className || '']
    .filter(Boolean)
    .join(' ');

  const inline = { width, height, ...(style || {}) };

  return (
    <span
      className={classNames}
      style={inline}
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={ariaLabel}
    />
  );
};

Skeleton.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  radius: PropTypes.oneOf(['sm', 'md', 'lg', 'pill', 'circle']),
  className: PropTypes.string,
  style: PropTypes.object,
  ariaLabel: PropTypes.string,
};

Skeleton.defaultProps = {
  width: '100%',
  height: '1rem',
  radius: 'sm',
  className: '',
  style: null,
  ariaLabel: 'Loading',
};

export default Skeleton;
